/*!
 * 金流帳務的可編輯儲存格 —— 「金流數據」與「雜項費用」兩頁共用。
 *
 * ⚠️ 這支原本在 TinyCashflow.js 與 TinyMiscFee.js **各實作一份**（連 fmt 與
 * 配色都各抄一次）。要改可見度時才發現要改兩個地方，所以抽出來。
 *
 * # 「可編輯」要靠格子本身的樣式表達
 *
 * 空的可編輯格原本只顯示「—」，那讀起來是「沒有資料」而不是「這裡可以填」——
 * 會計看到一整排「—」不會知道那是留給他的。所以：
 *
 *     空格          虛線框（像輸入框）＋ 鉛筆
 *     有值          數字 ＋ 虛線下底線 ＋ 鉛筆
 *     沒有權限      純文字，完全不給可編輯的暗示
 *
 * 鉛筆只是輔助，不是唯一線索。
 *
 * # withMonth：佣金那種「月結但填在任意一週」的項目
 *
 * 幣商佣金是月結，但會計填在哪一週不固定（實測 8 次裡有 2 次不在月底那週）。
 * 以前是靠「你點了哪一列」推出歸屬月份 —— 位置即語意，所以位置不能自由，
 * UI 反而逼他填在不自然的地方。改成明講：週隨他，月份在編輯時**填**
 * （年+月，type="month"）。重複填與未結束的月份都只告警、不阻擋。
 */
import React, { useEffect, useRef, useState } from 'react';

const C = {
  border: 'rgba(255,255,255,0.1)',
  inputBg: '#1a1f2b',
  text: '#fff',
  sub: 'rgba(255,255,255,0.8)',
  dim: 'rgba(255,255,255,0.4)',
  // 「這格可以填」的提示邊框。比 border 亮，但還沒亮到搶走數字的注意力。
  editable: 'rgba(255,255,255,0.22)',
  editableHover: 'rgba(116,132,255,0.75)',
  warning: '#fb6340',
};

const fmt = (n) => (n === null || n === undefined || Number.isNaN(Number(n))
  ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));

function CashflowEditCell({
  value, note, month, defaultMonth, monthNotice, onSave, canWrite,
  withNote = false, withMonth = false,
  // 期間已結束、該填還沒填（v2.46.0）：空格改成醒目的橘色「待填」，
  // 會計一打開就知道哪幾格是欠著的，不用再另外維護 Excel 對照。
  overdue = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [monthDraft, setMonthDraft] = useState('');
  const [hover, setHover] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  const empty = value === null || value === undefined;
  const tag = withMonth && month
    ? <span style={{ fontSize: 10, color: C.dim, marginLeft: 5 }}>{month}</span> : null;
  const noteLine = note
    ? <div style={{ fontSize: 10, color: C.dim, textAlign: 'right' }}>{note}</div> : null;

  const due = empty && overdue;

  if (!canWrite) {
    if (due) {
      return <span style={{ color: C.warning, fontWeight: 600 }}>待填{noteLine}</span>;
    }
    return (
      <span style={{ color: empty ? C.dim : C.sub }}>
        {empty ? '—' : fmt(value)}{tag}{noteLine}
      </span>
    );
  }

  if (!editing) {
    const line = hover ? C.editableHover : (due ? C.warning : C.editable);
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        <span
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onClick={() => {
            setDraft(empty ? '' : String(value));
            setNoteDraft(note || '');
            setMonthDraft(month || defaultMonth || '');
            setEditing(true);
          }}
          style={{
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
            justifyContent: 'flex-end',
            color: due ? C.warning : (empty ? C.dim : C.sub),
            fontWeight: due ? 600 : undefined,
            background: due ? 'rgba(251,99,64,0.12)' : undefined,
            border: empty ? `1px ${due ? 'solid' : 'dashed'} ${line}` : 'none',
            borderBottom: `1px dashed ${line}`,
            borderRadius: empty ? 4 : 0,
            padding: empty ? '1px 8px' : '0 0 1px',
            minWidth: empty ? 66 : undefined,
          }}>
          {empty ? (due ? '待填' : '') : fmt(value)}{tag}
          <i className="fa fa-pencil" style={{ fontSize: 11, color: due ? C.warning : (hover ? C.sub : C.dim) }} />
        </span>
        {noteLine}
      </span>
    );
  }

  const commit = () => {
    setEditing(false);
    onSave(draft.trim() === '' ? null : Number(draft),
      noteDraft.trim() || null,
      withMonth ? (monthDraft || null) : undefined);
  };
  const keys = (e) => {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') setEditing(false);
  };
  const inputStyle = {
    background: C.inputBg, color: C.text, border: `1px solid ${C.border}`,
    borderRadius: 4, padding: '3px 7px', fontSize: 13,
  };
  const notice = withMonth && monthNotice ? monthNotice(monthDraft) : null;
  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
      <input ref={ref} value={draft} onChange={(e) => setDraft(e.target.value)}
        onKeyDown={keys} style={{ ...inputStyle, width: 110, textAlign: 'right' }} />
      {withMonth && (
        // 歸屬月份用**填的**不用選的。下拉選單要嘛太長（列 15 個月）、要嘛不夠用
        //（補很舊的帳時剛好沒有那個選項），而且「這個月為什麼不在選單裡」
        // 比「填了會怎樣」更難懂。type="month" 就是年 + 月，格式不會填錯。
        // ⚠️ 重複填不擋，只告警 —— 覆蓋是合法操作（更正金額），
        //    月份還沒結束也不擋，只提醒系統算的應付還不準。
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
          <input type="month" value={monthDraft} onKeyDown={keys}
            onChange={(e) => setMonthDraft(e.target.value)}
            style={{ ...inputStyle, width: 110, fontSize: 12 }} />
          {notice && (
            <div style={{
              fontSize: 10, color: C.warning, maxWidth: 190,
              textAlign: 'right', lineHeight: 1.45,
            }}>{notice}</div>
          )}
        </div>
      )}
      {withNote && (
        <input value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)}
          placeholder="備註（例：7月）" onKeyDown={keys}
          style={{ ...inputStyle, width: 110, fontSize: 12 }} />
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" className="btn btn-sm btn-success py-1 px-3 m-0"
          style={{ fontSize: 12 }} onClick={commit}>儲存</button>
        <button type="button" className="btn btn-sm btn-secondary py-1 px-3 m-0"
          style={{ fontSize: 12 }} onMouseDown={(e) => e.preventDefault()}
          onClick={() => setEditing(false)}>取消</button>
      </div>
    </div>
  );
}

export default CashflowEditCell;
